import { runInAction } from 'mobx';

import { DeliveryMethods, type ETACalculation } from '~/api/ETA';
import type { PickupPoint } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import type { EditAddressData } from '~/components/ModalDeliveryAddress/interface';
import {
  saveAuthorizedAddress,
  saveUnauthorizedAddress,
} from '~/components/ModalDeliveryAddress/saveAddressUtils';
import { cartStore, checkoutStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';

export interface CommitPickupSelectionParams {
  /** Click-and-collect point the order is being bound to. */
  point: PickupPoint;
  /** Address of that point, already resolved by the caller. */
  deliveryAddress: DeliveryAddress;
  /** Form fields to merge into the saved address; pass blanks outside the modal. */
  editAddressData: EditAddressData;
  /** ETA the caller has confirmed for this point. */
  etaCalculation: ETACalculation;
  /** Empty the cart because the order moves to a different store. */
  resetCart: boolean;
}

/**
 * Binds the order to a click-and-collect point: saves the address tagged with
 * the point's warehouse and switches the checkout over to self-collection.
 *
 * Shared by the delivery/pickup modal's Confirm button and by the in-store QR
 * link, which reach the same state through different routes — the modal from
 * the map, the link from a warehouse code. Keeping the committing half in one
 * place is what stops the two from drifting into different orders.
 *
 * Resolving the address and the ETA is the caller's job; by the time this runs
 * both are expected to be valid for `point`. The cart reset is atomic with the
 * warehouse commit, so there is no intermediate state where the new store is
 * live with a cart priced against the previous one.
 */
export async function commitPickupSelection({
  point,
  deliveryAddress,
  editAddressData,
  etaCalculation,
  resetCart,
}: CommitPickupSelectionParams): Promise<{
  savedAddress: DeliveryAddress;
  /** False when the server rejected the write and only this tab knows. */
  persisted: boolean;
}> {
  let savedAddress = deliveryAddress;
  let persisted = true;

  if (userStore.isAuthorized) {
    const { updatedDeliveryAddress, persisted: written } =
      await saveAuthorizedAddress(deliveryAddress, editAddressData, {
        alwaysSetDeliveryAddress: true,
        usage: 'pickup',
        warehouseCode: point.warehouseCode,
      });
    savedAddress = updatedDeliveryAddress;
    persisted = written;
  } else {
    saveUnauthorizedAddress(
      deliveryAddress,
      editAddressData,
      'pickup',
      point.warehouseCode,
    );
  }

  runInAction(() => {
    orderStore.setEtaCalculation(etaCalculation);
    // requestETA's normal path also refreshes this alongside etaCalculation;
    // this shortcut skips requestETA, so it has to do the same or the pickup
    // slot picker keeps offering the previous pickup point's windows.
    checkoutStore.setDeliverySlots(etaCalculation.deliverySlots ?? {});
    checkoutStore.setDeliveryMethod(DeliveryMethods.CLICK_AND_COLLECT);
    if (resetCart) {
      cartStore.emptyCart();
    }
  });

  return { savedAddress, persisted };
}
